<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1000px"
    v-if="isDataLoadedFromServer"
  >
    <div class="bg-white poppins">
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">Edit Courier Location Mappings</h4>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 mx-xl-15 py-7">
        <!--begin::Body-->
        <form class="ma-0 pa-0 row">
          <div class="col-12 col-sm-6">
            <v-select
              v-model="formData.carrier_id"
              :label="$t('carrier')"
              item-text="text"
              item-value="index"
              :items="serverData.carriers"
              clearable
              outlined
              dense
              :error-messages="carrier_idErrors"
              @input="$v.formData.carrier_id.$touch()"
              @blur="$v.formData.carrier_id.$touch()"
            ></v-select>
          </div>
          <div class="col-12 col-sm-6">
            <v-select
              v-model="formData.carrier_mapping_type"
              :label="$t('carrier_mapping_type')"
              item-text="text"
              item-value="index"
              :items="serverData.carrier_mapping_types"
              clearable
              outlined
              dense
              :error-messages="carrier_mapping_typeErrors"
              @input="$v.formData.carrier_mapping_type.$touch()"
              @blur="$v.formData.carrier_mapping_type.$touch()"
            ></v-select>
          </div>
          <div class="col-12 col-sm-12 col-md-6">
            <v-text-field
              v-model="formData.carrier_value"
              :label="$t('carrier_value')"
              clearable
              outlined
              dense
              :error-messages="carrier_valueErrors"
              @input="$v.formData.carrier_value.$touch()"
              @blur="$v.formData.carrier_value.$touch()"
            ></v-text-field>
          </div>
          <div class="col-12 col-sm-6">
            <v-select
              v-model="formData.usage_type_id"
              :label="$t('usage_type')"
              item-text="text"
              item-value="index"
              :items="serverData.usage_types"
              clearable
              outlined
              dense
              :error-messages="usage_type_idErrors"
              @input="$v.formData.usage_type_id.$touch()"
              @blur="$v.formData.usage_type_id.$touch()"
            ></v-select>
          </div>
          <div class="col-12 col-sm-6">
            <v-select
              v-model="formData.mapping_type"
              :label="$t('mapping_type')"
              item-text="text"
              item-value="index"
              :items="serverData.types"
              clearable
              outlined
              dense
              :error-messages="mapping_typeErrors"
              @input="$v.formData.mapping_type.$touch()"
              @blur="$v.formData.mapping_type.$touch()"
            ></v-select>
          </div>

          <!-- address -->
          <input-group :label="$t('location')" class="col-12 ma-0 pa-0">
            <div class="col-6 my-0 py-0" v-if="formData.mapping_type >= 0">
              <v-autocomplete
                @change="onCountryChange"
                v-model="$v.formData.country.$model"
                :label="$t('country')"
                item-text="text"
                item-value="index"
                :items="serverData.countries"
                clearable
                outlined
                dense
                :error-messages="countryErrors"
                @input="$v.formData.country.$touch()"
                @blur="$v.formData.country.$touch()"
              ></v-autocomplete>
            </div>
            <div class="col-6 my-0 py-0" v-if="formData.mapping_type >= 1">
              <v-autocomplete
                v-model="$v.formData.state.$model"
                :items="serverData.states"
                :disabled="isStatesLoaded"
                item-text="title"
                item-value="id"
                :label="$t('state')"
                persistent-hint
                outlined
                dense
                :error-messages="stateErrors"
                @input="$v.formData.state.$touch()"
                @blur="$v.formData.state.$touch()"
                @change="onStateChange"
              >
              </v-autocomplete>
            </div>
            <div class="col-6 my-0 py-0" v-if="formData.mapping_type >= 2">
              <v-autocomplete
                v-model="$v.formData.city.$model"
                :items="serverData.cities"
                :disabled="isCitiesLoaded"
                item-text="title"
                item-value="id"
                :label="$t('city')"
                persistent-hint
                outlined
                dense
                :error-messages="cityErrors"
                @input="$v.formData.city.$touch()"
                @blur="$v.formData.city.$touch()"
                @change="onCityChange"
              >
              </v-autocomplete>
            </div>
            <div class="col-6 my-0 py-0" v-if="formData.mapping_type >= 3">
              <v-autocomplete
                :disabled="isAreasLoaded"
                v-model="formData.area"
                :items="serverData.areas"
                item-text="title"
                item-value="id"
                :label="$t('area')"
                persistent-hint
                outlined
                dense
              >
              </v-autocomplete>
            </div>
          </input-group>
          <!-- end address -->
        </form>
        <!--end::Body-->
        <div class="my-4 d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light mr-3 px-5 py-3 ls1"
            @click="resetEditForm"
          >
            Clear
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitEditForm"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import InputGroup from "./InputGroup.vue";

import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";

export default {
  name: "EditItem",
  components: { InputGroup },
  mixins: [validationMixin],
  validations() {
    return {
      formData: {
        carrier_id: { required },
        mapping_type: { required },
        iq_value_id: { required },
        carrier_value: { required },
        country: {
          required: requiredIf(() => {
            return this.formData.mapping_type >= 0;
          }),
        },
        state: {
          required: requiredIf(() => {
            return this.formData.mapping_type >= 1;
          }),
        },
        city: {
          required: requiredIf(() => {
            return this.formData.mapping_type >= 2;
          }),
        },
        area: {
          required: requiredIf(() => {
            return this.formData.mapping_type >= 3;
          }),
        },
        usage_type_id: { required },
        carrier_mapping_type: { required },
      },
    };
  },
  props: ["refresher", "pageLoader"],
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      id: null,
      carrier_id: null,
      mapping_type: null,
      iq_value_id: null,
      carrier_value: null,
      country: null,
      state: null,
      city: null,
      area: null,
      usage_type_id: null,
      carrier_mapping_type: null,
    },
  }),
  methods: {
    handleFormValidation(fieldName, vueObj) {
      const errors = [];
      if (!vueObj.$v.formData[fieldName].$dirty) return errors;
      if ("required" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      if ("url" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].url &&
          errors.push(this.$t("validation_this_url_is_invalid"));
      }
      if ("numeric" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].numeric &&
          errors.push(this.$t("validation_this_must_be_a_number"));
      }
      if ("email" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].email &&
          errors.push(this.$t("validation_this_email_address_is_invalid"));
      }
      return errors;
    },
    async onCountryChange(val, safe = false, is_id = false) {
      if (val) {
        this.pageLoader(true);
        let data = is_id ? { country: val } : { country: val };
        await ApiService.post("/address/states/search", data)
          .then((response) => {
            this.serverData.states = response.data.states;
            if (!safe) {
              this.formData.state = null;
              this.formData.city = null;
              this.formData.area = null;
              this.serverData.cities = null;
              this.serverData.areas = null;
            }
            let i = { ...this.serverData };
            this.serverData = i;
            this.pageLoader(false);
          })
          .catch(() => {
            this.pageLoader(false);

            this.dialog = false;
          });
      }
    },
    async onStateChange(val, safe = false, is_id = false) {
      if (val) {
        let data = { state: null };
        if (is_id) {
          data.state = val;
        } else {
          let item = null;
          // try {
          if (typeof val === "object" && val !== null) {
            item = this.serverData.states.filter(
              (state) => state.id == val.id
            )[0];
          } else {
            item = this.serverData.states.filter((state) => state.id == val)[0];
          }
          // } catch {
          // return;
          // }
          if (!item) {
            return;
          }
          data.state = item.id;
        }
        this.pageLoader(true);
        await ApiService.post("/address/cities/search", data)
          .then((response) => {
            this.serverData.cities = response.data.cities;
            if (!safe) {
              this.formData.city = null;
              this.formData.area = null;
              this.serverData.areas = null;
            }
            let i = { ...this.serverData };
            this.serverData = i;
            this.pageLoader(false);
          })
          .catch(() => {
            this.pageLoader(false);

            this.dialog = false;
          });
      }
    },
    async onCityChange(val, safe = false, is_id = false) {
      if (val) {
        let data = { city: null };
        if (is_id) {
          data.city = val;
        } else {
          let item = null;
          // try {
          if (typeof val === "object" && val !== null) {
            item = this.serverData.cities.filter(
              (city) => city.id == val.id
            )[0];
          } else {
            item = this.serverData.cities.filter((city) => city.id == val)[0];
          }
          // } catch {
          // return;
          // }
          if (!item) {
            return;
          }

          data.city = item.id;
        }
        this.pageLoader(true);
        await ApiService.post("/address/areas/search", data)
          .then((response) => {
            this.serverData.areas = response.data.areas;
            if (!safe) {
              this.formData.area = null;
            }
            let i = { ...this.serverData };
            this.serverData = i;
            this.pageLoader(false);
          })
          .catch(() => {
            this.pageLoader(false);
            this.dialog = false;
          });
      }
    },
    loadDataFromServer() {
      this.pageLoader(true);
      let data = { id: this.$store.getters.getItemForAction.id };
      ApiService.post("/shipping/courier_location_mappings/edit", data)
        .then((response) => {
          this.serverData = response.data;
          this.resetEditForm();
          if (this.formData.country) {
            this.onCountryChange(this.formData.country, true, true).then(() => {
              if (this.formData.state) {
                this.onStateChange(this.formData.state, true, true).then(() => {
                  if (this.formData.city) {
                    this.onCityChange(this.formData.city, true, true).then(
                      () => {
                        this.pageLoader(false);
                      }
                    );
                  }
                });
              }
            });
          } else {
            this.pageLoader(false);
          }
        })
        .catch(() => {
          this.dialog = false;
        })
        .finally(() => {
          // this.pageLoader(false);
        });
    },
    convertToFormData() {
      let data = {
        id: null,
        carrier_id: null,
        mapping_type: null,
        iq_value_id: null,
        carrier_value: null,
        usage_type_id: null,
        carrier_mapping_type: null,
      };
      data.id = this.formData.id;
      data.carrier_id = this.formData.carrier_id;
      data.mapping_type = this.formData.mapping_type;
      data.carrier_value = this.formData.carrier_value;
      data.usage_type_id = this.formData.usage_type_id;
      data.carrier_mapping_type = this.formData.carrier_mapping_type;

      if (this.formData.mapping_type == 0)
        data.iq_value_id = this.formData.country;
      else if (this.formData.mapping_type == 1)
        data.iq_value_id = this.formData.state;
      else if (this.formData.mapping_type == 2)
        data.iq_value_id = this.formData.city;
      else if (this.formData.mapping_type == 3)
        data.iq_value_id = this.formData.area;
      return data;
    },
    submitEditForm() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.pageLoader(true);
        let data = this.convertToFormData();
        ApiService.post("/shipping/courier_location_mappings/update", data)
          .then(() => {
            Swal.fire({
              title: "Updated",
              text: `Courier location mapping data has been updated`,
              icon: "success",
              showConfirmButton: false,
              timer: 2500,
            });
            this.toggleModal();
            this.pageLoader(false);
            this.refresher();
          })
          .catch(() => {
            this.pageLoader(false);
          });
      }
    },
    resetEditForm() {
      this.$v.$reset();
      if (this.serverData) {
        let copy = { ...this.serverData.carrier_location_mapping };
        Object.entries(this.formData).forEach((localD) => {
          Object.entries(this.serverData.carrier_location_mapping).forEach(
            (serverD) => {
              if (localD[0] === serverD[0]) {
                this.formData[localD[0]] = copy[serverD[0]];
              }
            }
          );
        });
      } else {
        this.formData = {
          id: null,
          carrier_id: null,
          mapping_type: null,
          iq_value_id: null,
          carrier_value: null,
          country: null,
          state: null,
          city: null,
          area: null,
          usage_type_id: null,
          carrier_mapping_type: null,
        };
      }
    },
    toggleModal() {
      if (this.dialog) {
        this.serverData = null;
        this.resetEditForm();
      } else this.loadDataFromServer();

      this.dialog = !this.dialog;
    },
  },
  computed: {
    isDataLoadedFromServer: function () {
      return !!this.serverData;
    },
    carrier_idErrors() {
      return this.handleFormValidation("carrier_id", this);
    },
    mapping_typeErrors() {
      return this.handleFormValidation("mapping_type", this);
    },
    iq_value_idErrors() {
      return this.handleFormValidation("iq_value_id", this);
    },
    carrier_valueErrors() {
      return this.handleFormValidation("carrier_value", this);
    },
    countryErrors() {
      return this.handleFormValidation("country", this);
    },
    stateErrors() {
      return this.handleFormValidation("state", this);
    },
    cityErrors() {
      return this.handleFormValidation("city", this);
    },
    areaErrors() {
      return this.handleFormValidation("area", this);
    },
    usage_type_idErrors() {
      return this.handleFormValidation("usage_type_id", this);
    },
    carrier_mapping_typeErrors() {
      return this.handleFormValidation("carrier_mapping_type", this);
    },
    states: function () {
      return this.serverData.states;
    },
    cities: function () {
      return this.serverData.cities;
    },
    areas: function () {
      return this.serverData.areas;
    },
    isStatesLoaded: function () {
      return !this.serverData.states;
    },
    isCitiesLoaded: function () {
      return !this.serverData.cities;
    },
    isAreasLoaded: function () {
      return !this.serverData.areas;
    },
  },
};
</script>

<style scoped>
.editorStyle {
  max-height: 70vh;
  overflow: hidden;
}
</style>
